import styled from 'styled-components';
import {
  APIFieldAttributes,
  APIFieldType,
  ArdoqId,
  PersistedFieldQuestion,
} from '@ardoq/api-types';
import { DateTimePicker } from '@ardoq/date-time-picker';
import { formatDateISO, parseLocaleDateString } from '@ardoq/date-time';
import { useEffectOnce } from '@ardoq/hooks';
import { FlexBox, Stack } from '@ardoq/layout';
import { Locale } from '@ardoq/locale';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';
import { getDateRangeType } from '@ardoq/date-range';
import { DateRange, FieldAnswer } from '../types';
import {
  SurveyWarningMessage,
  SurveyErrorMessage,
} from './surveyQuestionAtoms';
import { getDateFieldQuestionUserFeedback } from './getUserFeedback';
import { getFormatter } from './questionOperations';
import { isAnswerRequired } from '../utils/answerPredicates';
import { QuestionContentWrapper } from './questionsTypes';
import { SurveyReadOnlyLabel } from './SurveyReadOnlyQuestion';

const DateRangeDateContainer = styled.div`
  display: flex;
  flex-basis: 45%;
  & > div:last-child {
    flex: 1;
  }
`;

const getDateOrUndefined = (
  date: string | null | undefined,
  currentLocale: Locale
): Date | undefined => {
  return date
    ? (parseLocaleDateString(date, currentLocale) ?? undefined)
    : undefined;
};

export type SurveyDateRangeFieldQuestionProps = {
  question: PersistedFieldQuestion;
  answer: FieldAnswer<DateRange> | undefined;
  readonly?: boolean;
  value: DateRange | undefined;
  isLocked: boolean;
  currentLocale: Locale;
  i18nLocale?: I18nLocale;
  startDateField?: APIFieldAttributes;
  endDateField?: APIFieldAttributes;
  hideReadOnlyLabel: boolean;
  QuestionContentWrapper: QuestionContentWrapper;
  highlightUnansweredPriorityQuestions: boolean;
  setAnswer: (answer: { questionId: ArdoqId; value: DateRange }) => void;
};

export const SurveyDateRangeFieldQuestion = ({
  question,
  answer,
  readonly,
  value,
  isLocked,
  currentLocale,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
  startDateField,
  endDateField,
  hideReadOnlyLabel,
  QuestionContentWrapper,
  highlightUnansweredPriorityQuestions,
  setAnswer,
}: SurveyDateRangeFieldQuestionProps) => {
  const questionId = question._id;
  const type = startDateField
    ? getDateRangeType(startDateField.type)
    : undefined;
  const validators = question.validators ?? [];
  const isDateOnlyRange = type === APIFieldType.DATE_ONLY_RANGE;
  const isReadonlyOrLocked = readonly || isLocked;
  const formatter = getFormatter(isDateOnlyRange);

  useEffectOnce(() => {
    setAnswer({
      questionId,
      value: {
        startDate:
          value?.startDate === undefined
            ? (startDateField?.defaultValue as string | null | undefined)
            : value.startDate,
        endDate:
          value?.endDate === undefined
            ? (endDateField?.defaultValue as string | null | undefined)
            : value.endDate,
      },
    });
  });

  const getFieldControl = () => {
    return (
      <Stack gap="small">
        <FlexBox justify="space-between">
          <DateRangeDateContainer>
            <DateTimePicker
              label={t(
                i18nLocale,
                'surveysUi:dateRange.startDate',
                'Start date'
              )}
              date={value && getDateOrUndefined(value.startDate, currentLocale)}
              config={{
                formatter,
                locale: currentLocale,
                useTimePicker: !isDateOnlyRange,
                maxDate:
                  value && getDateOrUndefined(value.endDate, currentLocale),
              }}
              onChange={(newVal: Date | null) => {
                setAnswer({
                  questionId,
                  value: {
                    ...value,
                    startDate: isDateOnlyRange
                      ? newVal && formatDateISO(newVal)
                      : newVal && newVal.toISOString(),
                  },
                });
              }}
              isReadOnly={isReadonlyOrLocked}
              changeValueOnInvalidDate
            />
          </DateRangeDateContainer>
          <DateRangeDateContainer>
            <DateTimePicker
              label={t(i18nLocale, 'surveysUi:dateRange.endDate', 'End date')}
              date={value && getDateOrUndefined(value.endDate, currentLocale)}
              config={{
                formatter,
                locale: currentLocale,
                useTimePicker: !isDateOnlyRange,
                minDate:
                  value && getDateOrUndefined(value.startDate, currentLocale),
              }}
              onChange={(newVal: Date | null) => {
                setAnswer({
                  questionId,
                  value: {
                    ...value,
                    endDate: isDateOnlyRange
                      ? newVal && formatDateISO(newVal)
                      : newVal && newVal.toISOString(),
                  },
                });
              }}
              isReadOnly={isReadonlyOrLocked}
              changeValueOnInvalidDate
            />
          </DateRangeDateContainer>
        </FlexBox>
        {isReadonlyOrLocked && !hideReadOnlyLabel ? (
          <SurveyReadOnlyLabel i18nLocale={i18nLocale} />
        ) : null}
      </Stack>
    );
  };

  const isRequired = !isLocked && isAnswerRequired(validators);

  const errorsAndWarning = getDateFieldQuestionUserFeedback({
    type,
    isRequired,
    value,
    i18nLocale,
  });
  return (
    <QuestionContentWrapper
      question={question}
      answer={answer}
      highlightUnansweredPriorityQuestions={
        highlightUnansweredPriorityQuestions
      }
      i18nLocale={i18nLocale}
    >
      {type ? getFieldControl() : null}
      {errorsAndWarning?.warning && (
        <SurveyWarningMessage>{errorsAndWarning.warning}</SurveyWarningMessage>
      )}
      {errorsAndWarning?.error && (
        <SurveyErrorMessage>{errorsAndWarning.error}</SurveyErrorMessage>
      )}
    </QuestionContentWrapper>
  );
};
